import { deriveSubtaskDependenciesFromUows } from "@arti/runtime-shared/planRevision/uowDependencyProjection";
import { hasValue } from "@arti/utilities";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import type {
  PlanSnapshot,
  PlanSnapshotExtensivePlan,
  PlanSnapshotGroupedColumn,
  PlanSnapshotNode,
  PlanSnapshotNodeWithDependencyCounts,
  PlanSnapshotSummary,
} from "../types";

import {
  getPlanSnapshotByIdAsUser,
  getPlanSnapshotByIssueKeyAsUser,
  getPlanSnapshotByStoryIssueKeyAsUser,
  listRecentPlanSnapshotsAsUser,
} from "../dashboard-client";

interface PlanSnapshotsState {
  snapshots: PlanSnapshotSummary[];
  snapshot: PlanSnapshot | null;
  listLoading: boolean;
  snapshotLoading: boolean;
  error: string | null;
}

interface PlanSnapshotDerivedState {
  columns: PlanSnapshotGroupedColumn[];
  nodeById: Map<string, PlanSnapshotNodeWithDependencyCounts>;
  nodeByIssueKey: Map<string, PlanSnapshotNodeWithDependencyCounts>;
  dependentsByNodeId: Map<string, PlanSnapshotNodeWithDependencyCounts[]>;
}

const INITIAL_STATE: PlanSnapshotsState = {
  error: null,
  listLoading: false,
  snapshot: null,
  snapshotLoading: false,
  snapshots: [],
};

const SNAPSHOT_ID_PARAM = "snapshot_id";
const STORY_ISSUE_KEY_PARAM = "story_issue_key";
const ISSUE_KEY_PARAM = "issue_key";
const LIMIT_PARAM = "limit";
const NODE_ID_PARAM = "node_id";
const ATTACHMENT_PARAM = "attachment";
const VIEW_PARAM = "view";

export type PlanSnapshotViewMode = "kanban" | "dag";

function parseViewMode(value: string | null): PlanSnapshotViewMode {
  return value === "dag" ? "dag" : "kanban";
}

function parseNumberParam(value: string | null, fallback: number): number {
  if (!value) {
    return fallback;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function updateParams(
  current: URLSearchParams,
  updates: Record<string, string | null>,
): URLSearchParams {
  const next = new URLSearchParams(current);
  for (const [key, value] of Object.entries(updates)) {
    if (value === null || value === "") {
      next.delete(key);
    } else {
      next.set(key, value);
    }
  }
  return next;
}

export function usePlanSnapshots() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState<PlanSnapshotsState>(INITIAL_STATE);
  const hasAutoSelectedSnapshot = useRef(false);

  const snapshotId = searchParams.get(SNAPSHOT_ID_PARAM);
  const storyIssueKey = searchParams.get(STORY_ISSUE_KEY_PARAM);
  const issueKey = searchParams.get(ISSUE_KEY_PARAM);
  const recentLimit = parseNumberParam(searchParams.get(LIMIT_PARAM), 20);
  const selectedNodeId = searchParams.get(NODE_ID_PARAM);
  const selectedAttachment = searchParams.get(ATTACHMENT_PARAM);
  const viewMode = parseViewMode(searchParams.get(VIEW_PARAM));

  const loadRecentSnapshots = useCallback(async () => {
    setState((current) => ({ ...current, error: null, listLoading: true }));
    try {
      const { data } = await listRecentPlanSnapshotsAsUser({
        query: { include_tree: "false", limit: String(recentLimit) },
      });
      setState((current) => ({
        ...current,
        listLoading: false,
        snapshots: data.snapshots,
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setState((current) => ({
        ...current,
        error: message,
        listLoading: false,
      }));
    }
  }, [recentLimit]);

  const loadSnapshot = useCallback(
    async (query: {
      snapshotId?: string | null;
      storyIssueKey?: string | null;
      issueKey?: string | null;
    }) => {
      const filters = [
        query.snapshotId,
        query.storyIssueKey,
        query.issueKey,
      ].filter(Boolean);
      if (filters.length !== 1) {
        setState((current) => ({
          ...current,
          snapshot: null,
          snapshotLoading: false,
        }));
        return;
      }

      setState((current) => ({
        ...current,
        error: null,
        snapshotLoading: true,
      }));

      try {
        let snapshot: PlanSnapshot;
        if (query.snapshotId) {
          const { data } = await getPlanSnapshotByIdAsUser({
            path: { snapshotId: query.snapshotId },
            query: { include_tree: "true" },
          });
          ({ snapshot } = data);
        } else if (query.storyIssueKey) {
          const { data } = await getPlanSnapshotByStoryIssueKeyAsUser({
            path: { storyIssueKey: query.storyIssueKey },
            query: { include_tree: "true" },
          });
          ({ snapshot } = data);
        } else if (query.issueKey) {
          const { data } = await getPlanSnapshotByIssueKeyAsUser({
            path: { issueKey: query.issueKey },
            query: { include_tree: "true" },
          });
          ({ snapshot } = data);
        } else {
          throw new Error(
            "Provide exactly one of snapshotId, storyIssueKey, or issueKey.",
          );
        }
        setState((current) => ({
          ...current,
          snapshot,
          snapshotLoading: false,
        }));

        if (!query.snapshotId && snapshot.snapshot_id) {
          setSearchParams(
            (current) =>
              updateParams(current, {
                [SNAPSHOT_ID_PARAM]: snapshot.snapshot_id,
                [STORY_ISSUE_KEY_PARAM]: null,
                [ISSUE_KEY_PARAM]: null,
              }),
            { replace: true },
          );
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        setState((current) => ({
          ...current,
          error: message,
          snapshot: null,
          snapshotLoading: false,
        }));
      }
    },
    [setSearchParams],
  );

  useEffect(() => {
    void loadRecentSnapshots();
  }, [loadRecentSnapshots]);

  useEffect(() => {
    void loadSnapshot({ issueKey, snapshotId, storyIssueKey });
  }, [snapshotId, storyIssueKey, issueKey, loadSnapshot]);

  useEffect(() => {
    if (hasAutoSelectedSnapshot.current) {
      return;
    }
    if (snapshotId || storyIssueKey || issueKey) {
      hasAutoSelectedSnapshot.current = true;
      return;
    }
    if (state.listLoading) {
      return;
    }
    if (state.snapshots.length === 0) {
      return;
    }
    hasAutoSelectedSnapshot.current = true;
    setSearchParams(
      (current) =>
        updateParams(current, {
          [SNAPSHOT_ID_PARAM]: state.snapshots[0].snapshot_id,
        }),
      { replace: true },
    );
  }, [
    snapshotId,
    storyIssueKey,
    issueKey,
    state.listLoading,
    state.snapshots,
    setSearchParams,
  ]);

  const refresh = useCallback(async () => {
    await loadRecentSnapshots();
    await loadSnapshot({ issueKey, snapshotId, storyIssueKey });
  }, [issueKey, loadRecentSnapshots, loadSnapshot, snapshotId, storyIssueKey]);

  const selectSnapshot = useCallback(
    (nextSnapshotId: string) => {
      setSearchParams(
        (current) =>
          updateParams(current, {
            [SNAPSHOT_ID_PARAM]: nextSnapshotId,
            [STORY_ISSUE_KEY_PARAM]: null,
            [ISSUE_KEY_PARAM]: null,
            [NODE_ID_PARAM]: null,
            [ATTACHMENT_PARAM]: null,
          }),
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const selectNode = useCallback(
    (nodeId: string | null) => {
      setSearchParams(
        (current) =>
          updateParams(current, {
            [NODE_ID_PARAM]: nodeId,
            [ATTACHMENT_PARAM]: null,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const selectAttachment = useCallback(
    (filename: string | null) => {
      setSearchParams(
        (current) => updateParams(current, { [ATTACHMENT_PARAM]: filename }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setViewMode = useCallback(
    (mode: PlanSnapshotViewMode) => {
      setSearchParams(
        (current) =>
          updateParams(current, {
            [VIEW_PARAM]: mode === "kanban" ? null : mode,
          }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const derived: PlanSnapshotDerivedState = useMemo(() => {
    const nodes = state.snapshot?.nodes ?? null;
    if (!nodes || nodes.length === 0) {
      return {
        columns: [],
        dependentsByNodeId: new Map(),
        nodeById: new Map(),
        nodeByIssueKey: new Map(),
      };
    }
    const projected = projectUowDependenciesOntoNodes(
      nodes,
      state.snapshot?.uow_plan ?? null,
    );
    return buildDerivedState(projected);
  }, [state.snapshot]);

  return useMemo(
    () => ({
      columns: derived.columns,
      dependentsByNodeId: derived.dependentsByNodeId,
      error: state.error,
      issueKey,
      listLoading: state.listLoading,
      loading: state.listLoading || state.snapshotLoading,
      nodeById: derived.nodeById,
      nodeByIssueKey: derived.nodeByIssueKey,
      refresh,
      selectAttachment,
      selectNode,
      selectSnapshot,
      selectedAttachment,
      selectedNodeId,
      setViewMode,
      snapshot: state.snapshot,
      snapshotId,
      snapshotLoading: state.snapshotLoading,
      snapshots: state.snapshots,
      storyIssueKey,
      viewMode,
    }),
    [
      derived,
      issueKey,
      selectedAttachment,
      selectedNodeId,
      snapshotId,
      state,
      storyIssueKey,
      viewMode,
      refresh,
      selectAttachment,
      selectNode,
      selectSnapshot,
      setViewMode,
    ],
  );
}

/**
 * Subtask nodes don't carry dependencies on the wire — those live on the UoW
 * plan. Project them onto nodes for views that consume `node.dependencies`.
 * Legacy snapshots that already have node-level deps are left untouched.
 */
function projectUowDependenciesOntoNodes(
  nodes: PlanSnapshotNode[],
  uowPlan: PlanSnapshotExtensivePlan | null,
): PlanSnapshotNode[] {
  if (!uowPlan || nodes.every((node) => node.dependencies.length > 0)) {
    return nodes;
  }
  const projection = deriveSubtaskDependenciesFromUows(
    nodes.map((node) => ({ id: node.node_id, uowIds: node.unit_of_work_ids })),
    uowPlan.units_of_work.map((uow) => ({
      dependencies: uow.dependencies,
      id: uow.unit_of_work_id,
    })),
  );
  const nodeById = new Map(nodes.map((node) => [node.node_id, node]));
  return nodes.map((node) => {
    if (node.dependencies.length > 0) {
      return node;
    }
    const dependencies = (projection.get(node.node_id) ?? [])
      .map((depNodeId) => nodeById.get(depNodeId))
      .filter((dep): dep is PlanSnapshotNode => Boolean(dep))
      .map((dep) => ({
        depends_on_key: dep.issue_key,
        depends_on_node_id: dep.node_id,
      }));
    return dependencies.length > 0 ? { ...node, dependencies } : node;
  });
}

function buildDerivedState(
  nodes: PlanSnapshotNode[],
): PlanSnapshotDerivedState {
  const byNodeId = new Map<string, PlanSnapshotNode>();
  const byIssueKey = new Map<string, PlanSnapshotNode>();
  for (const node of nodes) {
    byNodeId.set(node.node_id, node);
    byIssueKey.set(node.issue_key, node);
  }

  const dependentsCount = new Map<string, number>();
  const unresolvedDependencies = new Map<string, number>();
  const dependentsByNodeId = new Map<string, PlanSnapshotNode[]>();

  for (const node of nodes) {
    for (const dep of node.dependencies) {
      const resolvedNodeId =
        (dep.depends_on_node_id && byNodeId.has(dep.depends_on_node_id)
          ? dep.depends_on_node_id
          : undefined) ?? byIssueKey.get(dep.depends_on_key)?.node_id;

      if (!resolvedNodeId) {
        unresolvedDependencies.set(
          node.node_id,
          (unresolvedDependencies.get(node.node_id) ?? 0) + 1,
        );
        continue;
      }

      dependentsCount.set(
        resolvedNodeId,
        (dependentsCount.get(resolvedNodeId) ?? 0) + 1,
      );
      const list = dependentsByNodeId.get(resolvedNodeId);
      if (list) {
        list.push(node);
      } else {
        dependentsByNodeId.set(resolvedNodeId, [node]);
      }
    }
  }

  const nodesWithCounts: PlanSnapshotNodeWithDependencyCounts[] = nodes.map(
    (node) => ({
      ...node,
      dependents_count: dependentsCount.get(node.node_id) ?? 0,
      depends_on_count: node.dependencies.length,
      unresolved_dependency_count:
        unresolvedDependencies.get(node.node_id) ?? 0,
    }),
  );

  const nodeWithCountsByNodeId = new Map<
    string,
    PlanSnapshotNodeWithDependencyCounts
  >();
  const nodeWithCountsByIssueKey = new Map<
    string,
    PlanSnapshotNodeWithDependencyCounts
  >();
  for (const node of nodesWithCounts) {
    nodeWithCountsByNodeId.set(node.node_id, node);
    nodeWithCountsByIssueKey.set(node.issue_key, node);
  }

  const dependentsWithCountsByNodeId = new Map<
    string,
    PlanSnapshotNodeWithDependencyCounts[]
  >();
  for (const [nodeId, dependents] of dependentsByNodeId.entries()) {
    const enriched = dependents
      .map((dependent) => nodeWithCountsByNodeId.get(dependent.node_id))
      .filter(hasValue);
    dependentsWithCountsByNodeId.set(nodeId, enriched);
  }

  const grouped = new Map<string, PlanSnapshotNodeWithDependencyCounts[]>();
  for (const node of nodesWithCounts) {
    const key = node.classification || "uncategorized";
    const bucket = grouped.get(key);
    if (bucket) {
      bucket.push(node);
    } else {
      grouped.set(key, [node]);
    }
  }

  const columns: PlanSnapshotGroupedColumn[] = Array.from(
    grouped.entries(),
  ).map(([classification, groupNodes]) => {
    groupNodes.sort(
      (a, b) =>
        a.sort_order - b.sort_order || a.issue_key.localeCompare(b.issue_key),
    );
    const dependencyCount = groupNodes.reduce(
      (acc, node) => acc + node.depends_on_count,
      0,
    );
    const dependentCount = groupNodes.reduce(
      (acc, node) => acc + node.dependents_count,
      0,
    );
    return {
      classification,
      dependency_count: dependencyCount,
      dependent_count: dependentCount,
      nodes: groupNodes,
      title: classification,
    };
  });

  columns.sort((a, b) => a.title.localeCompare(b.title));
  return {
    columns,
    dependentsByNodeId: dependentsWithCountsByNodeId,
    nodeById: nodeWithCountsByNodeId,
    nodeByIssueKey: nodeWithCountsByIssueKey,
  };
}
